<script setup lang="ts">
// MemberScopeSelector — v3's permission `<o-custom-select>` plus its two conditional
// `<o-custom-multi-select>` blocks (`UserPermissionModal.vue:81-132`), which also hand-rendered a
// second row of removable `o-badge`s under each because the multi-select did not show its own
// selection. 02's `MultiSelect` renders removable chips itself, so that duplicate row is gone.
//
// TWO things this component deliberately does NOT do:
//
//   * it does not own the scope TRANSITION. v3 hung a `watch` on `formData.level` (`:582-587`) and
//     was saved from clobbering the edit pre-selection only by declaration order — the watcher was
//     registered AFTER the seeding call, so its first run merely recorded a baseline. That is a
//     load-bearing accident. Here the scope arrives as a plain prop and a change is an EMIT, so the
//     modal applies the transition in an event handler that seeding never routes through. There is
//     no ordering to preserve and no `immediate` flag to get wrong.
//   * it does not derive roles. `MemberRolePicker` owns that, from the catalog.
//
// The dashboard scope is DISABLED when no dashboard is selectable. `useMemberDashboards` returns
// `undefined` for an unreadable catalog and `[]` for a genuinely empty workspace (D14's
// containment); either way a dashboard grant could not pass validation, so offering the scope would
// only produce "Please select Dashboard" with an empty list to choose from.
import { computed } from 'vue'

import type { MemberDashboardRef, MemberScope, MemberWorkspaceRef } from '../../models/member.types'
import { MEMBER_SCOPES } from '../../models/memberCatalog'

import { useI18n } from '@/shared/i18n'
import { MultiSelect, Select, type SelectOption } from '@/shared/ui'

const workspaceIds = defineModel<(string | number)[]>('workspaceIds', { default: () => [] })
const dashboardIds = defineModel<(string | number)[]>('dashboardIds', { default: () => [] })

const props = withDefaults(
  defineProps<{
    scope: MemberScope
    workspaces: readonly MemberWorkspaceRef[]
    /** `undefined` ⇒ the catalog could not be read — NOT "this workspace has none". */
    dashboards: readonly MemberDashboardRef[] | undefined
    disabled?: boolean
    /**
     * Which scopes to OFFER. Absent ⇒ the whole catalog, which is the web pane's behaviour and
     * must stay so. MCP-only passes `['account', 'workspace']` (ORP-21470): that product has no
     * dashboards at all, so a dashboard grant is not merely empty there, it is meaningless.
     * `MEMBER_SCOPES` is FILTERED, never edited — the catalog is the shared vocabulary and the
     * dashboard scope is still real everywhere else.
     */
    scopes?: readonly MemberScope[] | undefined
  }>(),
  { disabled: false },
)

const emit = defineEmits<{ 'update:scope': [scope: MemberScope] }>()

const { t } = useI18n()

const offeredScopes = computed(() =>
  props.scopes === undefined
    ? MEMBER_SCOPES
    : MEMBER_SCOPES.filter((entry) => props.scopes?.includes(entry.scope)),
)

/** Gated on the scope being OFFERED at all: a surface that hides the dashboard option must not
 *  then explain why the option it never showed is unusable. */
const dashboardsUnavailable = computed(
  () =>
    offeredScopes.value.some((entry) => entry.scope === 'dashboard') &&
    (props.dashboards?.length ?? 0) === 0,
)

const scopeOptions = computed<SelectOption<string>[]>(() =>
  offeredScopes.value.map((entry) => ({
    value: entry.scope,
    label: t(entry.labelKey),
    disabled: entry.scope === 'dashboard' && dashboardsUnavailable.value,
  })),
)

const workspaceOptions = computed<SelectOption<string | number>[]>(() =>
  props.workspaces.map((workspace) => ({ value: workspace.id, label: workspace.label })),
)

const dashboardOptions = computed<SelectOption<string | number>[]>(() =>
  (props.dashboards ?? []).map((dashboard) => ({ value: dashboard.id, label: dashboard.name })),
)

function onScope(value: string | null): void {
  if (value !== null) emit('update:scope', value as MemberScope)
}
</script>

<template>
  <div class="scope-selector flex flex-col gap-sm">
    <Select
      :model-value="props.scope"
      :options="scopeOptions"
      :label="t('workspaceSettings.permissions.invite.scopeLabel')"
      :disabled="props.disabled"
      data-testid="member-scope-select"
      @update:model-value="onScope"
    />

    <p
      v-if="dashboardsUnavailable"
      class="scope-selector__note m-0 text-muted font-sans text-[0.75rem]"
      data-testid="member-scope-no-dashboards"
    >
      {{ t('workspaceSettings.permissions.invite.noDashboards') }}
    </p>

    <MultiSelect
      v-if="props.scope === 'workspace'"
      v-model="workspaceIds"
      :options="workspaceOptions"
      :label="t('workspaceSettings.permissions.invite.workspaceLabel')"
      :placeholder="t('workspaceSettings.permissions.invite.workspacePlaceholder')"
      searchable
      :disabled="props.disabled"
      data-testid="member-workspace-select"
    />

    <MultiSelect
      v-if="props.scope === 'dashboard'"
      v-model="dashboardIds"
      :options="dashboardOptions"
      :label="t('workspaceSettings.permissions.invite.dashboardLabel')"
      :placeholder="t('workspaceSettings.permissions.invite.dashboardPlaceholder')"
      searchable
      :disabled="props.disabled"
      data-testid="member-dashboard-select"
    />
  </div>
</template>
